import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import {
  OverallSyllabusChecklistSchema,
  PointSyllabusInteractionSchema,
  SubTopicSyllabusInteractionSchema,
  SyllabusChecklistSubjectSchema,
  TopicSyllabusInteractionSchema,
} from "@tsTypes/sylllabus-checklist-schema";
import { RootState } from "../../app/store";
import { createMissingSyllabusMeta } from "./syllabusCheckllistHelpers";

const initialState: OverallSyllabusChecklistSchema = {};

export const subjectSyllabusChecklistSlice = createSlice({
  name: "subjectSyllabusChecklist",
  initialState,
  reducers: {
    setSyllabusState: (
      state,
      action: PayloadAction<
        | {
            [subjectDocumentId: string]:
              | SyllabusChecklistSubjectSchema
              | undefined;
          }
        | undefined
      >
    ) => {
      return { ...action.payload };
    },
    topicSyllabusSelection: (
      state,
      action: PayloadAction<TopicSyllabusInteractionSchema>
    ) => {
      const { subjectDocumentId, topicDocumentId, topicMeta } = action.payload;
      createMissingSyllabusMeta(state, subjectDocumentId, topicDocumentId);
      // if ticking the topic so all subtopics are to be selected
      if (
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal !==
        topicMeta.total
      ) {
        // update data
        state[subjectDocumentId]![topicDocumentId]!.data = topicMeta.subTopics;
        // update total
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal =
          topicMeta.total;
      }
      // if un-ticking the topic so all subtopics are to be un-selected
      else {
        // remove data
        state[subjectDocumentId]![topicDocumentId]!.data = {};
        // remove total
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal = 0;
      }
    },
    subTopicSyllabusSelection: (
      state,
      action: PayloadAction<SubTopicSyllabusInteractionSchema>
    ) => {
      const {
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        subTopicSyllabusPointDocumentIds,
      } = action.payload;
      createMissingSyllabusMeta(
        state,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId
      );
      // if ticking the subtopic so all points are to be selected
      if (
        state[subjectDocumentId]![topicDocumentId]!.data[subTopicDocumentId]!
          .length !== subTopicSyllabusPointDocumentIds.length
      ) {
        // update total - MUST COME BEFORE BELOW
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal +=
          subTopicSyllabusPointDocumentIds.length -
          state[subjectDocumentId]![topicDocumentId]!.data[subTopicDocumentId]!
            .length;
        // update data
        state[subjectDocumentId]![topicDocumentId]!.data[subTopicDocumentId] =
          subTopicSyllabusPointDocumentIds;
      }
      // if un-ticking the subtopic so all points are to be un-selected
      else {
        // remove data
        state[subjectDocumentId]![topicDocumentId]!.data[subTopicDocumentId] =
          [];
        // remove total
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal -=
          subTopicSyllabusPointDocumentIds.length;
      }
    },
    pointSyllabusSelection: (
      state,
      action: PayloadAction<PointSyllabusInteractionSchema>
    ) => {
      const {
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        pointDocumentId,
      } = action.payload;
      createMissingSyllabusMeta(
        state,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId
      );
      // if ticking the point
      if (
        !state[subjectDocumentId]![topicDocumentId]!.data[
          subTopicDocumentId
        ]!.includes(pointDocumentId)
      ) {
        // add point to data
        state[subjectDocumentId]![topicDocumentId]!.data[
          subTopicDocumentId
        ]!.push(pointDocumentId);
        // update total by 1
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal++;
      }
      // if un-ticking the point
      else {
        // remove point
        state[subjectDocumentId]![topicDocumentId]!.data[subTopicDocumentId] =
          state[subjectDocumentId]![topicDocumentId]!.data[
            subTopicDocumentId
          ]!.filter((p) => p !== pointDocumentId);
        // down total by 1
        state[subjectDocumentId]![topicDocumentId]!.selectedTotal--;
      }
    },
  },
});

export const {
  topicSyllabusSelection,
  subTopicSyllabusSelection,
  pointSyllabusSelection,
  setSyllabusState,
} = subjectSyllabusChecklistSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export function selectSyllabusState(state: RootState) {
  return state.subjectSyllabus;
}

export const selectTopicSyllabusMetaById = (
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string
) => state.subjectSyllabus[subjectDocumentId]?.[topicDocumentId];

export const selectSubTopicSyllabusMetaById = (
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string
) =>
  state.subjectSyllabus[subjectDocumentId]?.[topicDocumentId]?.data[
    subTopicDocumentId
  ];

export default subjectSyllabusChecklistSlice.reducer;
